import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ExpandMore, LocationOn, People, TrendingDown, TrendingUp } from '@mui/icons-material';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '...';

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDate = (dateString) => {
  const match = String(dateString).match(/Date\((\d+),(\d+),(\d+)\)/);
  if (!match) return dateString || 'Unknown date';

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month), Number(day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatReturn = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '...';

  return `${amount >= 0 ? '+' : ''}${amount.toFixed(0)}%`;
};

const getPlayerHighlights = (playerDetails = []) => {
  const sortedPlayers = [...playerDetails].sort((a, b) => Number(b.winnings) - Number(a.winnings));
  return {
    winner: sortedPlayers[0],
    biggestMelt: sortedPlayers[sortedPlayers.length - 1],
  };
};

export default function GameSummaryCard({ game, isLatest, isBiggestPot }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { winner, biggestMelt } = getPlayerHighlights(game.playerDetails);
  const recapId = `game-recap-${game.gameNumber}`;

  return (
    <article className={`game-summary-card${isLatest ? ' game-summary-card-latest' : ''}${isBiggestPot ? ' game-summary-card-biggest-pot' : ''}`}>
      {isLatest && <span className="game-summary-badge">Latest Game</span>}
      {isBiggestPot && <span className="game-summary-badge game-summary-badge-pot">Biggest Pot</span>}

      <div className="game-summary-card-header">
        <div className="game-summary-card-meta">
          <span className="game-number">{game.gameNumber}</span>
          <div>
            <h2>{formatDate(game.date)}</h2>
            <div className="game-summary-detail">
              <LocationOn aria-hidden="true" />
              <span>{game.location || 'Unknown location'}</span>
            </div>
            <div className="game-summary-detail game-summary-detail-muted">
              <People aria-hidden="true" />
              <span>{game.playersPlaying || game.playerDetails?.length || 0} Players</span>
            </div>
          </div>
        </div>
        <div className="game-pot">
          <span>Pot Size</span>
          <strong>{formatCurrency(game.moneyOnTable)}</strong>
        </div>
      </div>

      <div className="game-highlights">
        <div className="game-highlight game-highlight-winner">
          <span className="game-highlight-mark" role="img" aria-label="gold medal">🥇</span>
          <div className="game-highlight-body">
            <div className="game-highlight-label">
              <span>Winner</span>
              <TrendingUp aria-hidden="true" />
            </div>
            <div className="game-highlight-value">
              <strong>{winner?.playerName || '...'}</strong>
              <b>{winner ? `+${formatCurrency(winner.winnings)}` : '...'}</b>
            </div>
          </div>
        </div>
        <div className="game-highlight game-highlight-melt">
          <span className="game-highlight-mark" role="img" aria-label="poop">💩</span>
          <div className="game-highlight-body">
            <div className="game-highlight-label">
              <span>Loser</span>
              <TrendingDown aria-hidden="true" />
            </div>
            <div className="game-highlight-value">
              <strong>{biggestMelt?.playerName || '...'}</strong>
              <b>{biggestMelt ? formatCurrency(biggestMelt.winnings) : '...'}</b>
            </div>
          </div>
        </div>
      </div>

      <button
        className="game-summary-expand"
        type="button"
        aria-expanded={isExpanded}
        aria-controls={recapId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <span>{isExpanded ? 'Hide full recap' : 'View full recap'}</span>
        <ExpandMore className={isExpanded ? 'game-summary-expand-icon-open' : ''} aria-hidden="true" />
      </button>

      <div
        className={`game-recap${isExpanded ? ' game-recap-open' : ''}`}
        id={recapId}
        aria-hidden={!isExpanded}
      >
        <div className="game-player-results">
          <div className="game-player-table-scroll">
            <table className="game-player-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Winnings</th>
                  <th>Return</th>
                </tr>
              </thead>
              <tbody>
                {[...(game.playerDetails || [])]
                  .sort((a, b) => Number(b.winnings) - Number(a.winnings))
                  .map((player, index) => {
                    const isWinner = index === 0;
                    const isLoser = index === game.playerDetails.length - 1;
                    const winnings = Number(player.winnings);

                    return (
                      <tr className={isWinner ? 'game-player-row-winner' : isLoser ? 'game-player-row-loser' : ''} key={`${player.playerName}-${index}`}>
                        <td>
                          <span className="game-player-name">{player.playerName}</span>
                          {isWinner && <span className="game-player-mark" role="img" aria-label="gold medal">🥇</span>}
                          {isLoser && <span className="game-player-mark" role="img" aria-label="loser">💩</span>}
                        </td>
                        <td>{formatCurrency(player.totalMoneyIn)}</td>
                        <td>{formatCurrency(player.totalMoneyOut)}</td>
                        <td className={winnings >= 0 ? 'positive-result' : 'negative-result'}>
                          {winnings >= 0 ? '+' : ''}{formatCurrency(winnings)}
                        </td>
                        <td className={winnings >= 0 ? 'positive-result' : 'negative-result'}>
                          {formatReturn(player.return)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </article>
  );
}

GameSummaryCard.propTypes = {
  game: PropTypes.shape({
    gameNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string,
    location: PropTypes.string,
    playersPlaying: PropTypes.number,
    moneyOnTable: PropTypes.number,
    playerDetails: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  isLatest: PropTypes.bool,
  isBiggestPot: PropTypes.bool,
};

GameSummaryCard.defaultProps = {
  isLatest: false,
  isBiggestPot: false,
};